import React, { useCallback } from 'react';
import { Box, Popover, Stack, Typography } from '@mui/material';
import { Form, FormRenderProps } from 'react-final-form';
import { bindPopover } from 'material-ui-popup-state';

import { Button, Checkboxes, Select } from '@occc-app/design-system';
import { Product_Group_Key } from '@occc/graphql';

import { ProductGroupFilterFormValues } from './types';

import { productGroupKeysMap } from '../../../constants';

type ProductGroupFilterProps = {
  filter: ProductGroupFilterFormValues;
  popoverProps: ReturnType<typeof bindPopover>;
  setFilter: (value: ProductGroupFilterFormValues | undefined) => void;
};

const identifierFilterSelectOptions = [
  { label: 'View All', value: 'all' },
  { label: productGroupKeysMap[Product_Group_Key.Upc], value: Product_Group_Key.Upc },
  { label: productGroupKeysMap[Product_Group_Key.Sku], value: Product_Group_Key.Sku },
  {
    label: productGroupKeysMap[Product_Group_Key.FuelGradeId],
    value: Product_Group_Key.FuelGradeId,
  },
  { label: productGroupKeysMap[Product_Group_Key.Group], value: Product_Group_Key.Group },
];

export const ProductGroupFilter: React.FC<ProductGroupFilterProps> = ({
  filter,
  popoverProps,
  setFilter,
}) => {
  const onFilterSubmit = (values: ProductGroupFilterFormValues) => {
    setFilter(values);
    popoverProps.onClose();
  };

  const onFilterReset = useCallback(() => {
    setFilter({ identifier: 'all', isTarEligible: false });
    popoverProps.onClose();
  }, [popoverProps, setFilter]);

  const renderForm: FormRenderProps<ProductGroupFilterFormValues>['render'] = useCallback(
    ({ handleSubmit, form }: FormRenderProps<ProductGroupFilterFormValues>) => {
      const reset = () => {
        form.reset();
        onFilterReset();
      };

      return (
        <form onSubmit={handleSubmit}>
          <Stack px={3} pt={3}>
            <Typography paragraph mb={3}>
              Filter Product Groups
            </Typography>
            <Select name='identifier' label='Identifier' data={identifierFilterSelectOptions} />
            <Box mt={2}>
              <Checkboxes
                name='isTarEligible'
                data={{ label: 'Show TAR Eligible only', value: false }}
              />
            </Box>
          </Stack>
          <Box display='flex' justifyContent='flex-end' px={3} py={2}>
            <Button size='small' color='error' onClick={reset}>
              Reset
            </Button>
            <Button type='submit' size='small' color='primary'>
              Apply
            </Button>
          </Box>
        </form>
      );
    },
    [onFilterReset],
  );

  return (
    <Popover
      {...popoverProps}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      keepMounted
      PaperProps={{
        sx: {
          maxWidth: '460px',
          minWidth: '300px',
        },
      }}
    >
      <Form<ProductGroupFilterFormValues>
        render={renderForm}
        onSubmit={onFilterSubmit}
        initialValues={filter}
      />
    </Popover>
  );
};
